import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useApiKey } from '../contexts/ApiKeyContext';
import { HelpCircle, History, TrendingUp, TrendingDown, Eye, Clock, Sparkles, Trash2 } from 'lucide-react';

const PAGE_SIZE = 50;

const AgentFeedbackDashboard = ({ onShowHelp }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEval, setModalEval] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [selectedTools, setSelectedTools] = useState([]);
  const [minQualityScore, setMinQualityScore] = useState('');
  const [selectedMainIssue, setSelectedMainIssue] = useState('');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  
  // Feedback submission states
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackPrompt, setFeedbackPrompt] = useState('');
  const [feedbackResponse, setFeedbackResponse] = useState('');
  const [feedbackTool, setFeedbackTool] = useState('AgentFeedback');
  const [feedbackUseCase, setFeedbackUseCase] = useState('Agent Evaluation');
  const [feedbackAgentId, setFeedbackAgentId] = useState(''); // New state for selected agent
  const [feedbackResult, setFeedbackResult] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  
  // Delete functionality states
  const [deletingLog, setDeletingLog] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  
  // Agent management states
  const [agents, setAgents] = useState([]);
  const [validApiKeys, setValidApiKeys] = useState([]);

  // NEW: Prompt change visibility states
  const [showPromptChanges, setShowPromptChanges] = useState(false);
  const [promptRevisions, setPromptRevisions] = useState([]);
  const [loadingPromptHistory, setLoadingPromptHistory] = useState(false);
  const [selectedAgentForHistory, setSelectedAgentForHistory] = useState(null);
  const [showFullHistory, setShowFullHistory] = useState(false);

  // Get API key from context
  const { apiKey, error: apiKeyError } = useApiKey();

  // Extract unique values for filters
  const tools = useMemo(() => {
    const toolSet = new Set();
    logs.forEach(log => {
      if (log.tool) toolSet.add(log.tool);
    });
    return Array.from(toolSet).sort();
  }, [logs]);

  const mainIssues = useMemo(() => {
    const issueSet = new Set();
    logs.forEach(log => {
      if (log.main_issue) issueSet.add(log.main_issue);
    });
    return Array.from(issueSet).sort();
  }, [logs]);

  // NEW: Fetch prompt revision history for an agent
  const fetchPromptHistory = async (agentId) => {
    if (!agentId) return;
    
    setLoadingPromptHistory(true);
    try {
      const response = await axios.get(getApiUrl(`/agent/${agentId}/prompt/history`), {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200) {
        setPromptRevisions(response.data.revisions || []);
        setSelectedAgentForHistory(agents.find(a => a.id === agentId));
      }
    } catch (err) {
      console.error('Error fetching prompt history:', err);
    } finally {
      setLoadingPromptHistory(false);
    }
  };

  // Fetch available agents for this user
  const fetchAgents = async () => {
    try {
      const response = await axios.get(getApiUrl('/user/agents'), {
        withCredentials: true
      });
      
      if (response.data && response.data.agents) {
        setAgents(response.data.agents);
        console.log(`✅ Fetched ${response.data.count} agents for user`);
      } else {
        setAgents([]);
        console.log('✅ No agents found for user');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setAgents([]);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: PAGE_SIZE
      });

      if (selectedAgentId) {
        params.append('agent_id', selectedAgentId);
      }
      if (selectedTools.length > 0) {
        params.append('tool', selectedTools[0]); // Backend supports single tool filter
      }
      if (minQualityScore) {
        params.append('min_quality_score', minQualityScore);
      }
      if (selectedMainIssue) {
        params.append('main_issue', selectedMainIssue);
      }

      // Prepare request config
      const requestConfig = {
        params,
        withCredentials: true  // Always include credentials for session auth
      };
      
      // Only add API key header if we have one
      if (apiKey) {
        requestConfig.headers = {
          'x-api-key': apiKey
        };
      }
      
      const response = await axios.get(getApiUrl('/feedback/customer/logs'), requestConfig);

      setLogs(response.data.logs || []);
      setTotalPages(response.data.pages || 1);
      setTotalRecords(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch feedback logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Fetching agents...');
    fetchAgents().then(() => console.log('Agents fetched successfully')).catch(err => console.error('Error fetching agents:', err));
  }, []);

  useEffect(() => {
    console.log('Fetching logs with filters:', {
      currentPage,
      selectedTools,
      minQualityScore,
      selectedMainIssue,
      selectedAgentId
    });
    fetchLogs()
      .then(() => console.log('Logs fetched successfully'))
      .catch(err => console.error('Error fetching logs:', err));
  }, [currentPage, selectedTools, minQualityScore, selectedMainIssue, selectedAgentId]);

  useEffect(() => {
    const fetchValidApiKeys = async () => {
      try {
        const apiUrl = getApiUrl('/valid-api-keys');
        console.log('Fetching valid API keys from URL:', apiUrl);

        // Force Vercel deployment - updated API call with credentials
        const response = await axios.get(apiUrl, { withCredentials: true });
        console.log('Valid API keys fetched successfully:', response.data.keys);
        setValidApiKeys(response.data.keys || []);
      } catch (err) {
        console.error('Error fetching valid API keys:', err);
        console.error('Error response data:', err.response?.data);
        if (err.response?.status === 404) {
          console.warn('The valid API keys endpoint returned 404. Skipping to user-specific validation.');
          const userApiUrl = getApiUrl('/user/api-keys');
          console.log('Fetching user-specific API keys from URL:', userApiUrl);
          try {
            const userResponse = await axios.get(userApiUrl, { withCredentials: true });
            console.log('User-specific API keys fetched successfully:', userResponse.data.keys);
            setValidApiKeys(userResponse.data.keys || []);
          } catch (userErr) {
            console.error('Error fetching user-specific API keys:', userErr);
          }
        } else {
          console.error('Unexpected error while fetching valid API keys:', err);
        }
      }
    };

    fetchValidApiKeys();
  }, []);

  // Missing functions that were referenced but not defined
  const handleViewEvaluation = (log) => {
    setModalEval({
      evaluation: log.claude_evaluation,
      systemPrompt: log.system_prompt_used,
      prompt: log.prompt,
      response: log.response
    });
    setModalOpen(true);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleToolChange = (tool) => {
    setSelectedTools(prev => 
      prev.includes(tool) 
        ? prev.filter(t => t !== tool)
        : [...prev, tool]
    );
    setCurrentPage(1);
  };

  const handleMainIssueChange = (issue) => {
    setSelectedMainIssue(issue);
    setCurrentPage(1);
  };

  const handleMinQualityScoreChange = (score) => {
    setMinQualityScore(score);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedTools([]);
    setMinQualityScore('');
    setSelectedMainIssue('');
    setCurrentPage(1);
  };

  const submitFeedback = async () => {
    if (!feedbackPrompt || !feedbackResponse) {
      setFeedbackError('Please provide both prompt and response');
      return;
    }

    setSubmittingFeedback(true);
    setFeedbackError(null);
    setFeedbackResult(null);

    try {
      const response = await axios.post(getApiUrl('/feedback'), {
        prompt: feedbackPrompt,
        response: feedbackResponse,
        tool: feedbackTool,
        use_case: feedbackUseCase,
        agent_id: feedbackAgentId // Include the selected agent ID
      }, {
        headers: {
          'x-api-key': apiKey
        }
      });

      setFeedbackResult(response.data);
      
      // Clear form
      setFeedbackPrompt('');
      setFeedbackResponse('');
      setFeedbackTool('AgentFeedback');
      setFeedbackUseCase('Agent Evaluation');
      setFeedbackAgentId('');
      
      // Refresh the logs to show the new feedback
      fetchLogs();
      
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setFeedbackError(err.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const downloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      const params = new URLSearchParams();
      
      if (selectedAgentId) params.append('agent_id', selectedAgentId);
      if (selectedTools.length > 0) params.append('tool', selectedTools[0]);
      if (minQualityScore) params.append('min_quality_score', minQualityScore);
      if (selectedMainIssue) params.append('main_issue', selectedMainIssue);

      const response = await axios.get(getApiUrl('/feedback/customer/logs/csv'), {
        params,
        headers: {
          'x-api-key': apiKey
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agent-feedback-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading CSV:', err);
    } finally {
      setDownloadingCsv(false);
    }
  };

  // Delete feedback log function
  const handleDeleteLog = async (logId) => {
    if (!logId) return;
    
    setDeletingLog(logId);
    try {
      // Prepare request config
      const requestConfig = {
        withCredentials: true  // Always include credentials for session auth
      };
      
      // Only add API key header if we have one
      if (apiKey) {
        requestConfig.headers = {
          'x-api-key': apiKey
        };
      }
      
      const response = await axios.delete(getApiUrl(`/feedback/logs/${logId}`), requestConfig);
      
      if (response.status === 200) {
        // Remove the deleted log from the state
        setLogs(prevLogs => prevLogs.filter(log => log.id !== logId));
        setShowDeleteModal(false);
        setLogToDelete(null);
        console.log(`✅ Deleted feedback log ${logId}`);
      }
    } catch (err) {
      console.error('Error deleting feedback log:', err);
      alert(err.response?.data?.error || 'Failed to delete feedback log');
    } finally {
      setDeletingLog(null);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (log) => {
    setLogToDelete(log);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setLogToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Sort and filter logs based on current state
  const sortedLogs = useMemo(() => {
    let filteredLogs = [...logs];

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filteredLogs = filteredLogs.filter(log => 
        log.prompt?.toLowerCase().includes(searchLower) ||
        log.tool?.toLowerCase().includes(searchLower) ||
        log.use_case?.toLowerCase().includes(searchLower)
      );
    }

    // Apply tool filter
    if (selectedTools.length > 0) {
      filteredLogs = filteredLogs.filter(log => selectedTools.includes(log.tool));
    }

    // Apply quality score filter
    if (minQualityScore) {
      filteredLogs = filteredLogs.filter(log => 
        log.quality_score && log.quality_score >= parseInt(minQualityScore)
      );
    }

    // Apply main issue filter
    if (selectedMainIssue) {
      filteredLogs = filteredLogs.filter(log => log.main_issue === selectedMainIssue);
    }

    // Apply agent filter
    if (selectedAgentId) {
      filteredLogs = filteredLogs.filter(log => log.agent_id === selectedAgentId);
    }

    // Sort by quality score
    filteredLogs.sort((a, b) => {
      const scoreA = a.quality_score || 0;
      const scoreB = b.quality_score || 0;
      return sortAsc ? scoreA - scoreB : scoreB - scoreA;
    });

    return filteredLogs;
  }, [logs, search, selectedTools, minQualityScore, selectedMainIssue, selectedAgentId, sortAsc]);

  // NEW: Helper function to format prompt revision data
  const formatPromptRevision = (revision) => {
    return {
      id: revision.id,
      date: formatDate(revision.created_at),
      strategy: revision.strategy === 'auto' ? 'Auto-Healing' : 'Manual Suggestion',
      reason: revision.reason,
      oldPrompt: revision.old_prompt || 'No previous prompt',
      newPrompt: revision.new_prompt,
      analysis: revision.analysis_data ? JSON.parse(revision.analysis_data) : null
    };
  };

  // NEW: Helper function to get performance trend
  const getPerformanceTrend = (revisions) => {
    if (revisions.length < 2) return 'stable';
    
    const recentScores = revisions
      .slice(0, 3)
      .map(r => r.analysis?.average_score)
      .filter(score => score !== null && score !== undefined);
    
    if (recentScores.length < 2) return 'stable';
    
    const firstScore = recentScores[0];
    const lastScore = recentScores[recentScores.length - 1];
    
    if (lastScore > firstScore + 5) return 'improving';
    if (lastScore < firstScore - 5) return 'declining';
    return 'stable';
  };

  if (!apiKey || !validApiKeys.includes(apiKey)) {
    console.warn('Invalid or missing API key:', apiKey);
    console.warn('Valid API keys:', validApiKeys);
    return (
      <div className="max-w-7xl mx-auto p-6 overflow-hidden">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 10px rgba(123, 97, 255, 0.3)' }}>
          <h1 className="text-3xl font-bold text-white mb-6">Agent Feedback Dashboard</h1>
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6">
            <h3 className="font-semibold text-red-300 mb-2">API Key Required</h3>
            <p className="text-red-300">
              {apiKeyError || 'API key is required to access this dashboard. Please contact your admin.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 overflow-hidden">
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 10px rgba(123, 97, 255, 0.3)' }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Agent Feedback Dashboard</h1>
            <p className="text-gray-400 mt-2">Track, analyze, and submit feedback for your AI agents to improve their performance.</p>
          </div>
          {onShowHelp && (
            <button
              onClick={onShowHelp}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Help</span>
            </button>
          )}
        </div>
        
        {/* NEW: Prompt Change Visibility Section */}
        <div className="mb-8 bg-gray-800 border border-gray-600 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <History className="w-5 h-5 mr-2 text-purple-400" />
              Prompt Change History
            </h2>
            <div className="flex space-x-2">
              <select
                value={selectedAgentId}
                onChange={(e) => {
                  setSelectedAgentId(e.target.value);
                  if (e.target.value) {
                    fetchPromptHistory(e.target.value);
                  }
                }}
                className="px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              >
                <option value="">Select an Agent</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowPromptChanges(!showPromptChanges)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300 flex items-center"
              >
                <Eye className="w-4 h-4 mr-2" />
                {showPromptChanges ? 'Hide' : 'Show'} Changes
              </button>
            </div>
          </div>

          {showPromptChanges && selectedAgentId && (
            <div className="space-y-4">
              {loadingPromptHistory ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#7B61FF]"></div>
                  <p className="mt-2 text-gray-400">Loading prompt history...</p>
                </div>
              ) : promptRevisions.length === 0 ? (
                <div className="text-center py-8">
                  <History className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No prompt revisions found for this agent.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Performance Summary */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {selectedAgentForHistory?.name || 'Agent'} - Prompt Evolution
                        </h3>
                        <p className="text-sm text-gray-400">
                          {promptRevisions.length} revision(s) over time
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-white">
                            {promptRevisions.length}
                          </div>
                          <div className="text-xs text-gray-400">Revisions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-white">
                            {getPerformanceTrend(promptRevisions) === 'improving' ? (
                              <TrendingUp className="w-6 h-6 text-green-400" />
                            ) : getPerformanceTrend(promptRevisions) === 'declining' ? (
                              <TrendingDown className="w-6 h-6 text-red-400" />
                            ) : (
                              <Clock className="w-6 h-6 text-yellow-400" />
                            )}
                          </div>
                          <div className="text-xs text-gray-400">Trend</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Revisions */}
                  <div className="space-y-3">
                    {promptRevisions.slice(0, 3).map((revision, index) => {
                      const formatted = formatPromptRevision(revision);
                      return (
                        <div key={revision.id} className="bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                {index + 1}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-white">
                                  {formatted.strategy}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {formatted.date}
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 max-w-xs">
                              {formatted.reason}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div>
                              <div className="text-sm font-medium text-gray-300 mb-2">Previous Prompt</div>
                              <div className="bg-gray-800 rounded p-3 max-h-32 overflow-y-auto">
                                <pre className="text-xs text-gray-400 whitespace-pre-wrap">{formatted.oldPrompt}</pre>
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-300 mb-2">Updated Prompt</div>
                              <div className="bg-gray-800 rounded p-3 max-h-32 overflow-y-auto">
                                <pre className="text-xs text-gray-400 whitespace-pre-wrap">{formatted.newPrompt}</pre>
                              </div>
                            </div>
                          </div>

                          {formatted.analysis && (
                            <div className="mt-3 pt-3 border-t border-gray-600">
                              <div className="grid grid-cols-3 gap-4 text-xs">
                                <div>
                                  <div className="text-gray-400">Avg Score</div>
                                  <div className="text-white font-medium">
                                    {formatted.analysis.average_score?.toFixed(1) || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-400">Feedback Count</div>
                                  <div className="text-white font-medium">
                                    {formatted.analysis.feedback_count || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-400">Main Issues</div>
                                  <div className="text-white font-medium">
                                    {formatted.analysis.recent_issues?.slice(0, 2).map(issue => issue.issue).join(', ') || 'None'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {promptRevisions.length > 3 && (
                    <div className="text-center">
                      <button
                        onClick={() => setShowFullHistory(true)}
                        className="px-4 py-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        View all {promptRevisions.length} revisions →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Feedback Submission Form */}
        <div className="mb-8 bg-gray-800 border border-gray-600 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Submit Agent Feedback for Evaluation</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Agent Name
              </label>
              <select
                value={feedbackAgentId}
                onChange={(e) => setFeedbackAgentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              >
                <option value="">Select an Agent</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Use Case (Optional)
              </label>
              <input
                type="text"
                value={feedbackUseCase}
                onChange={(e) => setFeedbackUseCase(e.target.value)}
                placeholder="e.g., Customer Support"
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Agent Prompt *
            </label>
            <textarea
              value={feedbackPrompt}
              onChange={(e) => setFeedbackPrompt(e.target.value)}
              placeholder="Enter the prompt that was sent to your AI agent..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Agent Response *
            </label>
            <textarea
              value={feedbackResponse}
              onChange={(e) => setFeedbackResponse(e.target.value)}
              placeholder="Enter the response that your AI agent generated..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
            />
          </div>

          <button
            onClick={submitFeedback}
            disabled={submittingFeedback || !feedbackPrompt || !feedbackResponse || !feedbackAgentId}
            className="px-4 py-2 bg-[#7B61FF] text-white font-medium rounded-xl hover:bg-[#6B51EF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B61FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
          >
            {submittingFeedback ? 'Evaluating...' : 'Submit for Evaluation'}
          </button>

          {feedbackError && (
            <div className="mt-4 bg-red-900 border border-red-700 rounded-xl p-4">
              <p className="text-red-300">{feedbackError}</p>
            </div>
          )}

          {feedbackResult && (
            <div className="mt-4 bg-green-900 border border-green-700 rounded-xl p-4">
              <h3 className="font-semibold text-green-300 mb-2">Feedback Submitted Successfully!</h3>
              <p className="text-green-300">Quality Score: {feedbackResult.quality_score}</p>
              <p className="text-green-300">Main Issue: {feedbackResult.main_issue || 'None'}</p>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <input
              type="text"
              placeholder="Search by prompt, tool, or use case..."
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full md:w-96 px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
            />
            
            {/* Agent Filter */}
            <select
              value={selectedAgentId}
              onChange={(e) => { setSelectedAgentId(e.target.value); setCurrentPage(1); }}
              className="w-full md:w-64 px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
            >
              <option value="">All Agents</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            {tools.map(tool => (
              <button
                key={tool}
                onClick={() => handleToolChange(tool)}
                className={`px-3 py-1 text-sm font-medium rounded-xl transition-colors duration-300 ${
                  selectedTools.includes(tool)
                    ? 'bg-[#7B61FF] text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {tool}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Min Quality Score</label>
              <input
                type="number"
                min="0"
                max="100"
                value={minQualityScore}
                onChange={e => handleMinQualityScoreChange(e.target.value)}
                className="w-24 px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Main Issue</label>
              <select
                value={selectedMainIssue}
                onChange={e => handleMainIssueChange(e.target.value)}
                className="px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              >
                <option value="">All Issues</option>
                {mainIssues.map(issue => (
                  <option key={issue} value={issue}>{issue}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-600 rounded-xl hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B61FF] transition-colors duration-300"
            >
              Clear Filters
            </button>
            <button
              onClick={downloadCsv}
              disabled={downloadingCsv || logs.length === 0}
              className="px-4 py-2 bg-[#00CFFF] text-white font-medium rounded-xl hover:bg-[#00B8E6] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00CFFF] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors duration-300"
            >
              {downloadingCsv ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download CSV
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results */}
        <div>
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#7B61FF]"></div>
              <p className="mt-2 text-gray-400">Loading feedback logs...</p>
            </div>
          ) : error ? (
            <div className="bg-red-900 border border-red-700 rounded-xl p-4">
              <h3 className="font-semibold text-red-300 mb-2">Error</h3>
              <p className="text-red-300">{error}</p>
            </div>
          ) : (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-400">
                  Showing {sortedLogs.length} of {totalRecords} records
                </p>
              </div>

              {/* Table */}
              <div className="overflow-x-auto bg-gray-900 rounded-xl border border-gray-700" style={{ boxShadow: '0 0 10px rgba(123, 97, 255, 0.3)' }}>
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Prompt
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Tool
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Use Case
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => setSortAsc(s => !s)}>
                        Quality Score {sortAsc ? '▲' : '▼'}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Main Issue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        System Prompt
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-900 divide-y divide-gray-700">
                    {sortedLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {truncateText(log.prompt, 80)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {log.tool}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {log.use_case}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            log.quality_score >= 80 ? 'bg-green-900 text-green-300' :
                            log.quality_score >= 60 ? 'bg-yellow-900 text-yellow-300' :
                            'bg-red-900 text-red-300'
                          }`}>
                            {log.quality_score || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {log.main_issue || 'None'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          <span className="text-xs text-gray-400">
                            {log.system_prompt_used ? 
                              (log.system_prompt_used.length > 30 ? 
                                log.system_prompt_used.substring(0, 30) + '...' : 
                                log.system_prompt_used
                              ) : 
                              'Default'
                            }
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewEvaluation(log)}
                              className="text-[#7B61FF] hover:text-[#6B51EF] bg-gray-800 hover:bg-gray-700 px-3 py-1 rounded-xl text-xs font-medium transition-colors duration-300"
                            >
                              View Details
                            </button>
                            <button
                              onClick={() => openDeleteModal(log)}
                              className="text-red-400 hover:text-red-300 bg-gray-800 hover:bg-gray-700 px-3 py-1 rounded-xl text-xs font-medium transition-colors duration-300 flex items-center"
                              title="Delete this feedback record"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {sortedLogs.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-400">No feedback found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-xl text-white bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-xl text-white bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-400">
                        Showing page <span className="font-medium text-white">{currentPage}</span> of{' '}
                        <span className="font-medium text-white">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-xl border border-gray-600 bg-gray-800 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors duration-300 ${
                                page === currentPage
                                  ? 'z-10 bg-[#7B61FF] border-[#7B61FF] text-white'
                                  : 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-xl border border-gray-600 bg-gray-800 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Evaluation Modal */}
        {modalOpen && modalEval && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Feedback Evaluation Details</h2>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Prompt</h3>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap">{modalEval.prompt}</pre>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Response</h3>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap">{modalEval.response}</pre>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">System Prompt Used</h3>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap">{modalEval.systemPrompt || 'Default'}</pre>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Claude Evaluation</h3>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap">{modalEval.evaluation}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && logToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Delete Feedback Record</h2>
                <button
                  onClick={closeDeleteModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-300">
                  Are you sure you want to delete this feedback record? This action cannot be undone.
                </p>
                
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-2">Prompt:</div>
                  <div className="text-sm text-white">
                    {truncateText(logToDelete.prompt, 100)}
                  </div>
                  <div className="text-sm text-gray-400 mt-2">
                    Quality Score: {logToDelete.quality_score || 'N/A'} | 
                    Tool: {logToDelete.tool} | 
                    Date: {formatDate(logToDelete.created_at)}
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={closeDeleteModal}
                    className="flex-1 bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteLog(logToDelete.id)}
                    disabled={deletingLog === logToDelete.id}
                    className="flex-1 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingLog === logToDelete.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Prompt History Modal */}
        {showFullHistory && selectedAgentForHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Complete Prompt Revision History</h2>
                <button
                  onClick={() => setShowFullHistory(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">
                  {selectedAgentForHistory.name} - Complete Evolution Timeline
                </h3>
                <p className="text-sm text-gray-400">
                  {promptRevisions.length} total revisions from {formatDate(promptRevisions[promptRevisions.length - 1]?.created_at)} to {formatDate(promptRevisions[0]?.created_at)}
                </p>
              </div>

              <div className="space-y-4">
                {promptRevisions.map((revision, index) => {
                  const formatted = formatPromptRevision(revision);
                  return (
                    <div key={revision.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {promptRevisions.length - index}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">
                              {formatted.strategy}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatted.date}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 max-w-xs text-right">
                          {formatted.reason}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm font-medium text-gray-300 mb-2">Previous Prompt</div>
                          <div className="bg-gray-800 rounded p-3 max-h-48 overflow-y-auto">
                            <pre className="text-xs text-gray-400 whitespace-pre-wrap">{formatted.oldPrompt}</pre>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-300 mb-2">Updated Prompt</div>
                          <div className="bg-gray-800 rounded p-3 max-h-48 overflow-y-auto">
                            <pre className="text-xs text-gray-400 whitespace-pre-wrap">{formatted.newPrompt}</pre>
                          </div>
                        </div>
                      </div>

                      {formatted.analysis && (
                        <div className="mt-3 pt-3 border-t border-gray-600">
                          <div className="grid grid-cols-4 gap-4 text-xs">
                            <div>
                              <div className="text-gray-400">Avg Score</div>
                              <div className="text-white font-medium">
                                {formatted.analysis.average_score?.toFixed(1) || 'N/A'}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-400">Feedback Count</div>
                              <div className="text-white font-medium">
                                {formatted.analysis.feedback_count || 'N/A'}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-400">Main Issues</div>
                              <div className="text-white font-medium">
                                {formatted.analysis.recent_issues?.slice(0, 2).map(issue => issue.issue).join(', ') || 'None'}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-400">Revision #</div>
                              <div className="text-white font-medium">
                                {promptRevisions.length - index} of {promptRevisions.length}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentFeedbackDashboard;